import { useCallback, useEffect, useState } from 'react';
import { api, ErreurApi, type FicheCopropriete, type ListeCoproprietes } from './api';

/**
 * §12 — LE REGISTRE DES COPROPRIÉTÉS.
 *
 * ⚠️ LA SEULE CHOSE QUE CET ÉCRAN NE DOIT JAMAIS FAIRE : afficher « 0 lot ».
 * Une copropriété dont le règlement n'a pas été saisi et une copropriété sans
 * lot se ressemblent trait pour trait — un tableau vide — et disent l'inverse
 * l'une de l'autre. La seconde n'existe pas. Un acquéreur qui lit « 0 lot »
 * conclut à une maison individuelle et achète autre chose que ce qu'il croit.
 *
 * La phrase affichée vient du SERVICE (`structure.lecture`), pas d'ici :
 * la déduire dans le composant remettrait la règle à un endroit où elle se
 * recopie, et la phrase dangereuse serait à un copier-coller d'apparaître.
 */
export function Coproprietes({ onOuvrirParcelle }: { onOuvrirParcelle: (id: string) => void }) {
  const [critere, setCritere] = useState<'adresse' | 'parcelle' | 'identifiant'>('adresse');
  const [valeur, setValeur] = useState('');
  const [liste, setListe] = useState<ListeCoproprietes | null>(null);
  const [fiche, setFiche] = useState<FicheCopropriete | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [chargement, setChargement] = useState(false);

  const chercher = useCallback(async () => {
    setChargement(true); setErreur(null); setFiche(null);
    try {
      const q = new URLSearchParams();
      if (valeur.trim()) q.set(critere, valeur.trim());
      setListe(await api.coproprietes.chercher(q));
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e));
    } finally { setChargement(false); }
  }, [critere, valeur]);

  useEffect(() => { void chercher(); /* premier chargement : tout le registre */ }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const ouvrir = async (identifiant: string) => {
    setChargement(true); setErreur(null);
    try { setFiche(await api.coproprietes.fiche(identifiant)); }
    catch (e) { setErreur(e instanceof ErreurApi ? e.message : String((e as Error)?.message ?? e)); }
    finally { setChargement(false); }
  };

  return (
    <div className="tableau-bord">
      <div className="entete-scene">
        <h2>🏢 Copropriétés</h2>
        <p className="mention">
          Recherche par adresse, parcelle ou identifiant. Ce que vous voyez dépend de votre
          habilitation : l'existence d'un immeuble en copropriété est ouverte largement,
          son découpage en lots — qui dit qui possède quoi — ne l'est pas.
        </p>
      </div>

      <form className="filtres" onSubmit={(e) => { e.preventDefault(); void chercher(); }}>
        <label>Chercher par
          <select value={critere} onChange={(e) => setCritere(e.target.value as typeof critere)}>
            <option value="adresse">Adresse</option>
            <option value="parcelle">Parcelle</option>
            <option value="identifiant">Identifiant</option>
          </select>
        </label>
        <label>Valeur
          <input value={valeur} onChange={(e) => setValeur(e.target.value)}
                 placeholder={critere === 'parcelle' ? 'GN-PAR-…' : critere === 'identifiant' ? 'GN-COP-…' : 'rue, quartier…'} />
        </label>
        <button className="bouton">Chercher</button>
      </form>

      {erreur && <p className="erreur">{erreur}</p>}
      {chargement && <p className="attente">Interrogation du registre…</p>}

      {!chargement && !fiche && liste && (
        liste.coproprietes.length === 0
          ? <p className="indetermine">
              Aucune copropriété ne correspond. Le registre n'en connaît pas ici — ce qui ne prouve
              pas qu'il n'y en a pas : une copropriété non déclarée n'y figure simplement pas.
            </p>
          : (
            <table className="tableau">
              <thead><tr><th>Identifiant</th><th>Dénomination</th><th>Adresse</th><th>Parcelle</th><th>Structure</th></tr></thead>
              <tbody>
                {liste.coproprietes.map((c) => (
                  <tr key={c.identifiant}>
                    <td><button className="lien" onClick={() => void ouvrir(c.identifiant)}>{c.identifiant}</button></td>
                    <td>{c.denomination}</td>
                    <td>{c.adresse ?? '—'}</td>
                    <td><button className="lien" onClick={() => onOuvrirParcelle(c.parcelle)}>{c.parcelle}</button></td>
                    {/* ⚠️ JAMAIS UN NOMBRE SEUL DANS CETTE COLONNE. */}
                    <td>{c.structure.complete === false && c.lots_deposes === 0
                      ? <span className="etiquette">structure non déposée</span>
                      : `${c.lots_deposes} lot(s)${c.lots_declares ? ` / ${c.lots_declares} annoncés` : ''}`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
      )}

      {!chargement && fiche && <Fiche f={fiche} onRetour={() => setFiche(null)} onOuvrirParcelle={onOuvrirParcelle} />}
    </div>
  );
}

function Fiche({ f, onRetour, onOuvrirParcelle }: {
  f: FicheCopropriete; onRetour: () => void; onOuvrirParcelle: (id: string) => void;
}) {
  const c = f.copropriete;
  const structureMasquee = f.champs_masques.includes('lots');

  return (
    <>
      <div className="entete-fiche">
        <div>
          <h3>{c.denomination}</h3>
          <p className="mention">
            <code>{c.identifiant}</code> · {c.adresse ?? 'adresse non renseignée'} ·
            parcelle <button className="lien" onClick={() => onOuvrirParcelle(c.parcelle)}>{c.parcelle}</button>
          </p>
        </div>
        <button className="bouton discret" onClick={onRetour}>← Retour</button>
      </div>

      {/* NIVEAU 1 — l'existence, toujours rendue. */}
      <h4 className="titre-section">Existence</h4>
      <table className="tableau">
        <tbody>
          <tr><th>Régime</th><td>{c.regime ?? 'non renseigné'}</td></tr>
          <tr><th>Date du règlement</th><td>{c.date_reglement ?? 'non renseignée'}</td></tr>
          <tr><th>Statut</th><td>{c.statut}</td></tr>
          <tr><th>Lots annoncés au règlement</th><td>{c.lots_declares ?? 'non renseigné'}</td></tr>
          <tr><th>Lots saisis au registre</th><td>{c.lots_deposes}</td></tr>
        </tbody>
      </table>

      {/* ⚠️ LA PHRASE DU SERVICE, TELLE QUELLE. */}
      <p className={f.structure.complete === true ? 'rien-a-signaler' : 'structure-absente'}>
        {f.structure.lecture}
      </p>

      {structureMasquee ? (
        <p className="masque">
          Le découpage en lots, les tantièmes et les parties communes demandent une habilitation
          de niveau 2. Ce n'est pas que la structure est absente : c'est que vous n'avez pas le
          droit de la lire.
        </p>
      ) : (
        <>
          {f.tantiemes && (
            <>
              <h4 className="titre-section">Tantièmes</h4>
              <p className={f.tantiemes.complet ? 'rien-a-signaler' : f.tantiemes.parts === 0 ? 'indetermine' : 'avertissement'}>
                <strong className="fraction">{f.tantiemes.num}/{f.tantiemes.den}</strong> — {f.tantiemes.lecture}
              </p>
            </>
          )}

          <h4 className="titre-section">Lots</h4>
          {!f.lots || f.lots.length === 0
            ? <p className="structure-absente">{f.structure.lecture}</p>
            : (
              <table className="tableau">
                <thead><tr><th>N°</th><th>Type</th><th>Étage</th><th>Surface</th><th>Tantième</th>{f.lots.some((l) => l.bien_id !== undefined) && <th>Annonce</th>}</tr></thead>
                <tbody>
                  {f.lots.map((l) => (
                    <tr key={l.numero}>
                      <td><code>{l.numero}</code></td>
                      <td>{l.type}</td>
                      <td>{l.etage ?? '—'}</td>
                      <td>{l.surface_m2 === null ? '—' : `${l.surface_m2.toLocaleString('fr-FR')} m²`}</td>
                      <td className="fraction">{l.tantieme}</td>
                      {l.bien_id !== undefined && <td>{l.bien_id ? <code>{l.bien_id.slice(0, 8)}…</code> : '—'}</td>}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

          <h4 className="titre-section">Parties communes</h4>
          {!f.parties_communes || f.parties_communes.length === 0
            ? <p className="indetermine">Aucune partie commune saisie. Un immeuble en copropriété en comporte toujours — leur absence au registre est une lacune de saisie, pas leur inexistence.</p>
            : (
              <ul className="liste">
                {f.parties_communes.map((p) => (
                  <li key={p.designation}>
                    {p.designation} <em className="pastille">{p.categorie}</em>
                    {p.surface_m2 !== null && ` — ${p.surface_m2.toLocaleString('fr-FR')} m²`}
                  </li>
                ))}
              </ul>
            )}

          <h4 className="titre-section">Emprises bâties</h4>
          {!f.batiments || f.batiments.length === 0
            ? <p className="mention">Aucune emprise rattachée. La copropriété est posée sur la parcelle, sans que l'immeuble ait été désigné au plan.</p>
            : <ul className="liste">{f.batiments.map((b) => (
                <li key={b.id}><code>{b.identifiant}</code> — {b.usage ?? 'usage inconnu'}{b.niveaux ? `, ${b.niveaux} niveau(x)` : ''}</li>))}</ul>}
        </>
      )}

      <h4 className="titre-section">Documents rattachés à la parcelle</h4>
      {f.documents.length === 0
        ? <p className="mention">Aucun document au coffre pour cette parcelle — le règlement de copropriété n'y a pas été déposé.</p>
        : <ul className="liste">{f.documents.map((d) => (
            <li key={d.id}><code>{d.identifiant}</code> — {d.type_code} ({d.depose_le.slice(0, 10)})</li>))}</ul>}
    </>
  );
}
